import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../hooks";

interface TopNavBarProps {
  onLogout: () => void;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ onLogout }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const isAnonymous = authService.isAnonymousUser();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    // Add a small delay for better UX
    setTimeout(async () => {
      await onLogout();
      setIsLoggingOut(false);
    }, 500);
  };

  const handleLogin = async () => {
    // Clear anonymous session but keep share tokens for merging after login
    authService.clearAnonymousAuthKeepTokens();
    await refreshAuth();
    navigate("/");
  };

  return (
    <nav className="jp-topnav">
      <div data-section="top-nav-bar-shell" className="jp-topnav__inner">
          {/* joint.photos logo */}
          <div data-section="top-nav-bar-brand" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-transparent rounded-2xl flex items-center justify-center">
              <img
                src="/joint-photos-icon-fixed-colors.svg"
                alt="joint.photos"
                className="w-8 h-8 bg-transparent rounded-2xl"
              />
            </div>
            <div>
              <h1 className="text-xl font-extrabold tracking-tight text-white/95">joint.photos</h1>
            </div>
          </div>

          {/* User Actions */}
          <div data-section="top-nav-bar-actions" className="flex items-center gap-3">
            {/* Login/Logout Button */}
            {isAnonymous ? (
              <button
                onClick={handleLogin}
                className="jp-btn jp-btn--primary jp-btn--sm"
                aria-label="Login"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                <span className="hidden sm:inline">Log in</span>
              </button>
            ) : (
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="jp-btn jp-btn--ghost jp-btn--sm"
                aria-label="Logout"
              >
                {isLoggingOut ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Logging out…</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span className="hidden sm:inline">Logout</span>
                  </>
                )}
              </button>
            )}
          </div>
      </div>
    </nav>
  );
};

export default TopNavBar;
